/* ===========================================================
   sync/store.js — everything kept on this device (IndexedDB)

     docs       the doc list as last seen, plus text for offline search
     entries    each doc's Yjs updates; pending = not yet stored online
     meta       per doc: last server sequence number seen
     outbox     list changes made offline (create, rename, trash…)
     kv         small odds and ends (profile, theme, device id)
     files      handles for files opened from the computer
     conflicts  offline clashes still waiting for a choice

   An edit lands in `entries` within ~50 ms of typing it, before it
   is sent anywhere. That's the "never lose text" floor.
   MemoryStore (memstore.js) has the same shape for tests and guests.
   =========================================================== */

import { openDB, deleteDB, tx, get, getAll, put, add, del } from '../lib/idb.js';

export class IDBStore {
  constructor(name) { this.name = name; this.ready = openDB(name); }

  async run(names, mode, fn) { return tx(await this.ready, names, mode, fn); }

  /* ---------- doc content ---------- */

  entries(doc) {
    return this.run('entries', 'readonly', s => getAll(s.entries.index('doc'), doc))
      .then(rows => rows.sort((a, b) => a.k - b.k));
  }

  addEntry(doc, data, pending, cid = 0) {
    return this.run('entries', 'readwrite', s => add(s.entries, { doc, data, pending: pending ? 1 : 0, cid, at: Date.now() }));
  }

  ack(keys) {
    if (!keys.length) return Promise.resolve();
    return this.run('entries', 'readwrite', async s => {
      for (const k of keys) {
        const row = await get(s.entries, k);
        if (row && row.pending) { row.pending = 0; await put(s.entries, row); }
      }
    });
  }

  /** Swap every confirmed entry of a doc for one merged entry (keeps pending ones). */
  compactSynced(doc, data, upToKey) {
    return this.run('entries', 'readwrite', async s => {
      const rows = await getAll(s.entries.index('doc'), doc);
      for (const r of rows) if (!r.pending && r.k <= upToKey) await del(s.entries, r.k);
      await add(s.entries, { doc, data, pending: 0, cid: 0, at: Date.now() });
    });
  }

  async pendingDocs() {
    const rows = await this.run('entries', 'readonly', s => getAll(s.entries));
    return [...new Set(rows.filter(r => r.pending).map(r => r.doc))];
  }

  getMeta(doc) { return this.run('meta', 'readonly', s => get(s.meta, doc)); }
  setMeta(doc, patch) {
    return this.run('meta', 'readwrite', async s => {
      const cur = (await get(s.meta, doc)) || { doc, lastSeq: 0 };
      await put(s.meta, { ...cur, ...patch, doc });
    });
  }

  /** Forget a doc's local copy entirely (after it's deleted for good). */
  dropDoc(doc) {
    return this.run(['entries', 'meta', 'docs', 'conflicts'], 'readwrite', async s => {
      const rows = await getAll(s.entries.index('doc'), doc);
      for (const r of rows) await del(s.entries, r.k);
      await del(s.meta, doc);
      await del(s.docs, doc);
      await del(s.conflicts, doc);
    });
  }

  /* ---------- doc list ---------- */

  listDocs() { return this.run('docs', 'readonly', s => getAll(s.docs)); }
  getDocRec(id) { return this.run('docs', 'readonly', s => get(s.docs, id)); }
  putDocRec(rec) { return this.run('docs', 'readwrite', s => put(s.docs, rec)); }
  patchDocRec(id, patch) {
    return this.run('docs', 'readwrite', async s => {
      const cur = await get(s.docs, id);
      if (cur || patch.id) await put(s.docs, { ...(cur || {}), ...patch, id });
    });
  }
  delDocRec(id) { return this.run('docs', 'readwrite', s => del(s.docs, id)); }

  /* ---------- offline queue ---------- */

  addOp(op) { return this.run('outbox', 'readwrite', s => add(s.outbox, { ...op, at: Date.now() })); }
  ops() { return this.run('outbox', 'readonly', s => getAll(s.outbox)).then(r => r.sort((a, b) => a.k - b.k)); }
  removeOp(k) { return this.run('outbox', 'readwrite', s => del(s.outbox, k)); }

  /* ---------- odds and ends ---------- */

  getKV(k) { return this.run('kv', 'readonly', s => get(s.kv, k)).then(r => r?.v); }
  setKV(k, v) { return this.run('kv', 'readwrite', s => put(s.kv, { k, v })); }

  getConflicts(doc) { return this.run('conflicts', 'readonly', s => get(s.conflicts, doc)); }
  setConflicts(doc, v) { return this.run('conflicts', 'readwrite', s => put(s.conflicts, { ...v, doc })); }
  clearConflicts(doc) { return this.run('conflicts', 'readwrite', s => del(s.conflicts, doc)); }

  listFiles() { return this.run('files', 'readonly', s => getAll(s.files)); }
  getFile(key) { return this.run('files', 'readonly', s => get(s.files, key)); }
  putFile(rec) { return this.run('files', 'readwrite', s => put(s.files, rec)); }
  delFile(key) { return this.run('files', 'readwrite', s => del(s.files, key)); }

  async destroy() {
    (await this.ready).close();
    await deleteDB(this.name);
  }
}
